/**
 * Monthly calendar view for a driver's published rides.
 * Dots on days that have rides; tap a day to scroll a list of that day's rides
 * below the grid. Pure presentation — caller passes already-fetched ride list.
 */
import { useMemo, useState } from 'react';
import type { RideDto } from '@waylo/shared';
import { jsLocale, useT } from '../lib/i18n';
import { cn } from '../lib/cn';
import { WEEKDAYS_MON_FIRST, WEEKDAY_SHORT_KEYS } from '@waylo/shared';
import { RideCard } from './RideCard';

interface Props {
  rides: RideDto[];
  onOpenRide: (id: string) => void;
}

interface DayCell {
  date: Date;
  inMonth: boolean;
  rides: RideDto[];
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function shiftMonth(d: Date, delta: number): Date {
  const out = new Date(d);
  out.setDate(1);
  out.setMonth(out.getMonth() + delta);
  return out;
}

/**
 * Builds a 6-row × 7-col grid of DayCells covering the month containing `cursor`.
 * Rows always start on Monday (Mon-first per `WEEKDAYS_MON_FIRST`).
 */
function buildGrid(cursor: Date, ridesByDay: Map<string, RideDto[]>): DayCell[] {
  const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  // JS Date.getDay() returns 0=Sun..6=Sat; we want 0=Mon..6=Sun.
  const startOffset = (firstOfMonth.getDay() + 6) % 7;
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - startOffset);

  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push({
      date: d,
      inMonth: d.getMonth() === cursor.getMonth(),
      rides: ridesByDay.get(ymd(d)) ?? [],
    });
  }
  return cells;
}

export function RideCalendar({ rides, onOpenRide }: Props): JSX.Element {
  const { t, locale } = useT();
  const fmt = jsLocale(locale);
  const today = startOfDay(new Date());
  const [cursor, setCursor] = useState<Date>(today);
  const [selectedYmd, setSelectedYmd] = useState<string>(ymd(today));

  // Bucket rides by ymd in viewer-local time. (Server sends UTC departureAt;
  // toLocaleDateString in fmtLocale would group at user TZ, which is the right
  // grouping for "my schedule this week".)
  const ridesByDay = useMemo(() => {
    const m = new Map<string, RideDto[]>();
    for (const r of rides) {
      const d = new Date(r.departureAt);
      const key = ymd(d);
      const arr = m.get(key) ?? [];
      arr.push(r);
      m.set(key, arr);
    }
    return m;
  }, [rides]);

  const grid = useMemo(() => buildGrid(cursor, ridesByDay), [cursor, ridesByDay]);
  const selectedRides = ridesByDay.get(selectedYmd) ?? [];

  const monthLabel = cursor.toLocaleDateString(fmt, { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCursor(shiftMonth(cursor, -1))}
          aria-label="Previous month"
          className="h-9 w-9 rounded-full bg-tg-bg-secondary text-tg-text"
        >
          ‹
        </button>
        <p className="text-base font-semibold capitalize text-tg-text">{monthLabel}</p>
        <button
          type="button"
          onClick={() => setCursor(shiftMonth(cursor, +1))}
          aria-label="Next month"
          className="h-9 w-9 rounded-full bg-tg-bg-secondary text-tg-text"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs uppercase tracking-wide text-tg-hint">
        {WEEKDAYS_MON_FIRST.map((d) => (
          <div key={d}>{t(`weekday.${WEEKDAY_SHORT_KEYS[d]}` as 'weekday.mon')}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {grid.map((cell) => {
          const key = ymd(cell.date);
          const isToday = key === ymd(today);
          const isSelected = key === selectedYmd;
          const hasRides = cell.rides.length > 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelectedYmd(key)}
              className={cn(
                'relative flex h-10 items-center justify-center rounded-input text-sm transition',
                !cell.inMonth && 'text-tg-hint/50',
                cell.inMonth && !isSelected && 'text-tg-text',
                isSelected && 'bg-brand-green text-white font-semibold',
                !isSelected && isToday && 'ring-1 ring-brand-green',
                !isSelected && hasRides && 'bg-brand-green/10',
              )}
            >
              {cell.date.getDate()}
              {hasRides && !isSelected && (
                <span className="absolute bottom-1 inline-block h-1 w-1 rounded-full bg-brand-green" />
              )}
            </button>
          );
        })}
      </div>

      <div className="space-y-2 pt-2">
        {selectedRides.length === 0 ? (
          <p className="rounded-input bg-tg-bg-secondary p-3 text-center text-sm text-tg-hint">
            {t('calendar.empty')}
          </p>
        ) : (
          selectedRides.map((r) => (
            <RideCard key={r.id} ride={r} hideDriver onOpen={() => onOpenRide(r.id)} />
          ))
        )}
      </div>
    </div>
  );
}
